import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ChartLineUp, CircleNotch, GoogleLogo, ArrowSquareOut, CaretUp, CaretDown } from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";

const Metric = ({ label, value }) => (
  <div className="border p-4 bg-[var(--surface)]" style={{ borderColor: "var(--line)" }}>
    <div className="text-xs uppercase tracking-wider" style={{ color: "var(--text-2)" }}>{label}</div>
    <div className="mono text-2xl font-bold mt-2">{value}</div>
  </div>
);

export default function RankTracking() {
  const { loadWorkspaces, currentWorkspace } = useAuth();
  const [status, setStatus] = useState(null);
  const [sites, setSites] = useState([]);
  const [site, setSite] = useState("");
  const [days, setDays] = useState(28);
  const [dimension, setDimension] = useState("query");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [params, setParams] = useSearchParams();
  const [trend, setTrend] = useState([]);

  useEffect(() => {
    api.gscTrend().then((r) => setTrend(r.data?.rows || [])).catch(() => {});
  }, [currentWorkspace?.id]);

  const loadStatus = useCallback(async () => {
    try {
      const r = await api.gscStatus();
      setStatus(r.data);
      setSite(r.data.site_url || "");
      if (r.data.connected) {
        const s = await api.gscSites();
        setSites(s.data.sites || []);
      }
    } catch (e) { console.debug("GSC status unavailable:", e?.message); }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus, currentWorkspace?.id]);

  useEffect(() => {
    const g = params.get("gsc");
    if (g) {
      if (g === "connected") { toast.success("Google Search Console connected"); loadStatus(); }
      else toast.error(`Connection failed (${g})`);
      params.delete("gsc"); setParams(params, { replace: true });
    }
  }, [params, setParams, loadStatus]);

  const connect = async () => {
    try { const r = await api.gscAuthUrl(); window.location.href = r.data.authorization_url; }
    catch (e) { toast.error(apiError(e)); }
  };

  const chooseSite = async (url) => {
    setSite(url);
    try { await api.gscSelectSite(url); await loadWorkspaces(); toast.success("Property selected"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const runReport = async () => {
    if (!site) { toast.error("Select a property first"); return; }
    setLoading(true);
    try {
      const r = await api.gscRankings(Number(days), dimension);
      setData(r.data);
      api.gscTrend().then((t) => setTrend(t.data?.rows || [])).catch(() => {});
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  if (!status) return <div className="fade-up"><CircleNotch size={26} className="animate-spin" color="var(--accent)" /></div>;

  if (!status.configured) {
    return (
      <div className="fade-up max-w-2xl border p-8 bg-[var(--surface)]" style={{ borderColor: "var(--line)" }}>
        <ChartLineUp size={30} weight="duotone" color="var(--accent)" />
        <h2 className="text-lg font-bold mt-3">Rank Tracking needs Google setup</h2>
        <p className="text-sm mt-2" style={{ color: "var(--text-2)" }}>
          Automatic organic rank tracking uses the Google Search Console API. The workspace owner must add
          Google OAuth credentials (Client ID + Secret) to the backend. Once configured, connect your
          verified Search Console property here to see live positions, clicks, impressions and CTR.
        </p>
      </div>
    );
  }

  if (!status.connected) {
    return (
      <div className="fade-up max-w-2xl border p-8 bg-[var(--surface)] text-center" style={{ borderColor: "var(--line)" }}>
        <ChartLineUp size={34} weight="duotone" color="var(--accent)" className="mx-auto" />
        <h2 className="text-lg font-bold mt-3">Connect Google Search Console</h2>
        <p className="text-sm mt-2 mb-6" style={{ color: "var(--text-2)" }}>Authorize read-only access to pull real organic rankings for your verified property.</p>
        <button data-testid="gsc-connect" onClick={connect} className="inline-flex items-center gap-2 px-6 py-3 text-sm font-bold text-white hover:opacity-90 transition-opacity" style={{ background: "var(--primary)" }}>
          <GoogleLogo size={18} weight="bold" /> Connect Search Console
        </button>
      </div>
    );
  }

  return (
    <div className="fade-up space-y-6 max-w-6xl">
      <div className="border p-5 bg-[var(--surface)] flex flex-col md:flex-row md:items-end gap-4" style={{ borderColor: "var(--line)" }}>
        <div className="flex-1">
          <label className="text-xs uppercase tracking-wider" style={{ color: "var(--text-2)" }}>Property</label>
          <select data-testid="gsc-site-select" value={site} onChange={(e) => chooseSite(e.target.value)} className="w-full mono text-sm px-3 py-2.5 mt-1 border bg-[#0b1220] outline-none" style={{ borderColor: "var(--line)" }}>
            <option value="">Select verified site…</option>
            {sites.map((s) => <option key={s.siteUrl} value={s.siteUrl}>{s.siteUrl}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider" style={{ color: "var(--text-2)" }}>Range</label>
          <select value={days} onChange={(e) => setDays(e.target.value)} className="w-full text-sm px-3 py-2.5 mt-1 border bg-[#0b1220] outline-none" style={{ borderColor: "var(--line)" }}>
            {[7, 28, 90].map((d) => <option key={d} value={d}>Last {d} days</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider" style={{ color: "var(--text-2)" }}>Group by</label>
          <select data-testid="gsc-dimension" value={dimension} onChange={(e) => setDimension(e.target.value)} className="w-full text-sm px-3 py-2.5 mt-1 border bg-[#0b1220] outline-none" style={{ borderColor: "var(--line)" }}>
            <option value="query">Query</option>
            <option value="page">Page</option>
          </select>
        </div>
        <button data-testid="gsc-run" onClick={runReport} disabled={loading} className="px-6 py-2.5 text-sm font-bold flex items-center gap-2 justify-center text-white disabled:opacity-60 hover:opacity-90 transition-opacity" style={{ background: "var(--primary)" }}>
          {loading ? <CircleNotch size={16} className="animate-spin" /> : <ChartLineUp size={16} weight="bold" />} Run
        </button>
      </div>

      {trend.length >= 2 && (
        <div className="border p-6 bg-[var(--surface)]" style={{ borderColor: "var(--line)" }}>
          <h2 className="text-base font-bold mb-1">Average Position Trend</h2>
          <p className="text-xs mb-4" style={{ color: "var(--text-2)" }}>Lower is better — one point per report run.</p>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={trend.map((t) => ({ label: new Date(t.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }), avg_position: t.totals?.avg_position }))}>
              <XAxis dataKey="label" tick={{ fill: "#9CA3AF", fontSize: 11 }} axisLine={{ stroke: "#1f2937" }} tickLine={false} />
              <YAxis tick={{ fill: "#9CA3AF", fontSize: 11 }} axisLine={false} tickLine={false} reversed />
              <Tooltip contentStyle={{ background: "var(--surface)", border: "1px solid var(--line)", fontSize: 12 }} labelStyle={{ color: "#9CA3AF" }} />
              <Line type="monotone" dataKey="avg_position" stroke="var(--accent)" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Metric label="Clicks" value={data.totals.clicks} />
            <Metric label="Impressions" value={data.totals.impressions} />
            <Metric label="Avg Position" value={data.totals.avg_position} />
            <Metric label="Top 10 keywords" value={data.totals.top10} />
          </div>
          <div className="border bg-[var(--surface)] overflow-x-auto" style={{ borderColor: "var(--line)" }} data-testid="gsc-rankings-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="mono text-[11px] uppercase tracking-wider text-left" style={{ color: "var(--text-2)" }}>
                  <th className="p-3 border-b" style={{ borderColor: "var(--line)" }}>{dimension}</th>
                  <th className="p-3 border-b" style={{ borderColor: "var(--line)" }}>Position</th>
                  <th className="p-3 border-b" style={{ borderColor: "var(--line)" }}>Clicks</th>
                  <th className="p-3 border-b" style={{ borderColor: "var(--line)" }}>Impr.</th>
                  <th className="p-3 border-b" style={{ borderColor: "var(--line)" }}>CTR</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.key} className="hover:bg-[#0e1626] transition-colors">
                    <td className="p-3 border-b mono text-xs max-w-[320px] truncate" style={{ borderColor: "var(--line)" }} title={r.key}>{r.key}</td>
                    <td className="p-3 border-b mono font-bold" style={{ borderColor: "var(--line)", color: r.position <= 10 ? "var(--success)" : r.position <= 20 ? "var(--warning)" : "var(--danger)" }}>{r.position}</td>
                    <td className="p-3 border-b mono" style={{ borderColor: "var(--line)" }}>{r.clicks}</td>
                    <td className="p-3 border-b mono" style={{ borderColor: "var(--line)" }}>{r.impressions}</td>
                    <td className="p-3 border-b mono" style={{ borderColor: "var(--line)" }}>{r.ctr}%</td>
                  </tr>
                ))}
                {data.rows.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-sm" style={{ color: "var(--text-2)" }}>No data for this range yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
